import { BLOG_POSTS } from "../blogContent";

const COMPARISON_LINKS = [
	{
		label: "Camtasia alternative",
		href: "/camtasia-alternative/",
		description: "A lighter option for polished demos, tutorials, and product walkthroughs.",
	},
	{
		label: "OBS alternative",
		href: "/obs-alternative/",
		description: "For teams that want simpler setup and built-in editing after the recording ends.",
	},
	{
		label: "Loom alternative",
		href: "/loom-alternative/",
		description: "Compare a faster path to more polished async video.",
	},
	{
		label: "QuickTime alternative",
		href: "/quicktime-player-alternative/",
		description: "Built for Mac users who need more than raw screen capture.",
	},
	{
		label: "Cap alternative",
		href: "/cap-alternative/",
		description: "Native Linux support and on-device AI captions, compared honestly.",
	},
	{
		label: "Screen Studio alternative",
		href: "/screen-studio-alternative/",
		description: "The cinematic recording style on Windows and Linux, not just macOS.",
	},
];

const RELIABILITY_CHECKS = [
	{
		title: "Match the destination before recording",
		description:
			"Check the accepted container, video codec, audio codec, resolution, and frame-rate limits for the editor, browser, learning platform, or social network that will receive the file.",
	},
	{
		title: "Prefer a common delivery format",
		description:
			"MP4 is broadly supported, but the codec inside the file still matters. Confirm the destination's published requirements instead of assuming every MP4 will behave the same way.",
	},
	{
		title: "Test audio tracks and frame rate",
		description:
			"Export a short sample with microphone and system audio, then open it in the real destination. Editing tools can react differently to separate audio tracks or variable frame rates.",
	},
	{
		title: "Keep a high-quality master",
		description:
			"Preserve an original export before compressing or converting. A clean master lets you make a destination-specific copy without recording the session again.",
	},
];

export function BlogHubPage() {
	const [featuredPost, ...otherPosts] = BLOG_POSTS;

	return (
		<>
			<section className="relative overflow-hidden pb-20 pt-28 md:pb-24 md:pt-40">
				<div className="absolute inset-0 pointer-events-none">
					<div className="absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_45%)]" />
					<div className="absolute left-1/2 top-32 h-[420px] w-[780px] -translate-x-1/2 rounded-full bg-brand-500/[0.08] blur-[120px]" />
				</div>

				<div className="relative mx-auto max-w-6xl px-6">
					<div className="max-w-3xl">
						<div className="inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-500/10 px-4 py-1.5 text-xs font-medium text-brand-300">
							2026 guides and comparisons
						</div>
						<h1 className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl md:text-7xl">
							A screen recording blog built for{" "}
							<span className="gradient-text">buyers in 2026</span>
						</h1>
						<p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/58 md:text-xl">
							Explore source-linked buyer guides and practical how-to content for choosing and using
							screen recording software for demos, tutorials, bug reports, and async updates.
						</p>
						<a
							href="/about/"
							className="mt-5 inline-block text-sm text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200"
						>
							How Clapio researches and updates its guides
						</a>
						<a
							href="/blog/how-to-add-captions-to-a-screen-recording/"
							className="ml-0 mt-5 inline-block text-sm text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200 sm:ml-6"
						>
							How to add captions to a screen recording
						</a>
						<a
							href="/blog/how-to-record-a-gif-of-your-screen/"
							className="ml-0 mt-5 inline-block text-sm text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200 sm:ml-6"
						>
							How to record or capture a GIF from your screen
						</a>
					</div>

					<a
						href={featuredPost.href}
						className="mt-10 block rounded-[2rem] border border-white/[0.08] bg-white/[0.04] p-6 shadow-2xl shadow-black/20 transition-all hover:-translate-y-0.5 hover:border-brand-500/25 md:p-8"
					>
						<div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
							<span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1">
								Featured guide
							</span>
							<span>{featuredPost.category}</span>
							<span>•</span>
							<span>{featuredPost.readTime}</span>
							<span>•</span>
							<span>{featuredPost.updatedLabel}</span>
						</div>
						<h2 className="mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white md:text-4xl">
							{featuredPost.title}
						</h2>
						<p className="mt-4 max-w-3xl text-base leading-8 text-white/52 md:text-lg">
							{featuredPost.description}
						</p>
						<div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-300">
							Read the guide <span aria-hidden="true">→</span>
						</div>
					</a>
				</div>
			</section>

			<section className="pb-24 md:pb-28">
				<div className="mx-auto max-w-6xl px-6">
					<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
						{otherPosts.map((post) => (
							<a
								key={post.href}
								href={post.href}
								className="glass-card rounded-[1.6rem] p-6 transition-all hover:-translate-y-0.5 hover:border-brand-500/25"
							>
								<div className="flex flex-wrap items-center gap-2 text-xs text-white/40">
									<span>{post.category}</span>
									<span>•</span>
									<span>{post.readTime}</span>
								</div>
								<h2 className="mt-4 text-2xl font-semibold tracking-tight text-white/90">
									{post.title}
								</h2>
								<p className="mt-4 text-sm leading-7 text-white/48">{post.description}</p>
								<div className="mt-6 text-sm font-medium text-brand-300">Open article →</div>
							</a>
						))}
					</div>

					<section className="mt-16 rounded-[2rem] border border-white/[0.08] bg-white/[0.03] p-6 md:p-8">
						<p className="text-[11px] uppercase tracking-[0.18em] text-brand-300">
							Reliability checklist
						</p>
						<h2 className="mt-3 max-w-3xl text-2xl font-semibold tracking-tight text-white/90 md:text-3xl">
							How to avoid screen-recording codec compatibility problems
						</h2>
						<p className="mt-4 max-w-3xl text-sm leading-7 text-white/50 md:text-base">
							No recorder can guarantee compatibility with every editor and publishing platform.
							User reports are useful, but the most reliable check is a short end-to-end export
							using your actual audio sources and destination.
						</p>
						<div className="mt-8 grid gap-4 md:grid-cols-2">
							{RELIABILITY_CHECKS.map((check) => (
								<article
									key={check.title}
									className="rounded-[1.4rem] border border-white/[0.06] bg-black/10 p-5"
								>
									<h3 className="text-base font-semibold text-white/90">{check.title}</h3>
									<p className="mt-3 text-sm leading-7 text-white/48">{check.description}</p>
								</article>
							))}
						</div>
						<a
							href="/blog/best-screen-recording-software-2026/"
							className="mt-7 inline-flex text-sm font-medium text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200"
						>
							Compare screen recording software by workflow →
						</a>
					</section>

					<div className="mt-16 grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
						<div className="glass-card p-6 md:p-8">
							<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">
								Comparison pages
							</p>
							<h2 className="mt-3 text-2xl font-semibold tracking-tight text-white/90 md:text-3xl">
								Compare recorders by workflow
							</h2>
							<p className="mt-4 max-w-[46ch] text-sm leading-7 text-white/48 md:text-base">
								Use these comparisons to check platform support, editing models, sharing options,
								pricing, and the trade-offs between local and cloud-first workflows.
							</p>
						</div>

						<div className="grid gap-3 md:grid-cols-2">
							{COMPARISON_LINKS.map((link) => (
								<a
									key={link.href}
									href={link.href}
									className="glass-card rounded-[1.4rem] px-5 py-5 transition-all hover:-translate-y-0.5 hover:border-brand-500/25 active:translate-y-px"
								>
									<span className="block text-sm font-semibold text-white/90">{link.label}</span>
									<span className="mt-2 block text-sm leading-7 text-white/45">
										{link.description}
									</span>
								</a>
							))}
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
